import React, { useMemo, useState, useEffect } from "react";
import { Box, Drawer, Typography, Button, Slider } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import axios from "axios";
import { RotateCcw, Filter, ListFilter, Logs, CircleCheck, Layers, Briefcase, Globe, TrendingUp, ShieldCheck, BookOpen } from "lucide-react";
import CustomMultiSelect from "./Viewkpi/CustomMultiSelect";

const FilterDrawer = ({
  open,
  onClose,
  parentDomain,
  domain,
  subDomain,
  product,
  filterOptions,
  handleParentDomainChange,
  handleDomainChange,
  handleSubDomainChange,
  handleProductChange,
  handleResetFilters,
  hierarchicalFilters,
  selectedTab,
  handleApplyAllFilters,
  allSelectedFilters,
  searchQuery,
  // GLOSSARY
  glossaryFilterOptions,
  glossaryFilters,
  handleApplyGlossaryFilters,
}) => {
  const PRIMARY_COLOR = "#FF03F3";

  const formatEmailToName = (email) => {
    if (!email || typeof email !== "string") return email;

    if (!email.includes("@")) return email;

    const namePart = email.split("@")[0];

    const nameParts = namePart.split(".");

    const formattedName = nameParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");

    return formattedName;
  };

  const [localFilters, setLocalFilters] = useState({
    parentDomain: [],
    domain: [],
    subDomain: [],
    product: [],
    productDataLayer: [],
    lineOfBusiness: [],
    reportType: [],
    cloudProvider: [],
    tags: [],
    parentRegion: [],
    region: [],
    country: [],
    expectedUserBase: [0, 10000],
    dqRating: [0, 100],
    productOwner: [],
    assetSource: [],
    reportOwner: [],
    searchBy: "tables",
    cdeEnabled: false,
    tables: [],
    columns: [],
    searchQuery: "",
    // GLOSSARY
    status: [],
    assetType: [],
    glossaryDomain: [],
  });
  const [expandedSection, setExpandedSection] = useState();
  const [hoveredSection, setHoveredSection] = useState(null);

  const [tablesFromAPI, setTablesFromAPI] = useState([]);
  const [columnsFromAPI, setColumnsFromAPI] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#F4F6F8",
      borderColor: state.isFocused ? PRIMARY_COLOR : "#E5E7EB",
      borderWidth: state.isFocused ? 2 : 1,
      borderRadius: 8,
      minHeight: 42,
      boxShadow: state.isFocused ? `0 0 0 1px #bb1ccc` : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#bb1ccc" : "#CBD5F5",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(0, 0, 160, 0.1)",
      borderRadius: 4,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#bb1ccc",
      fontSize: 10,
      fontWeight: 500,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#bb1ccc",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 160, 0.2)",
        color: "#bb1ccc",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9CA3AF",
      fontSize: 10,
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: 8,
      boxShadow: "0 4px 14px rgba(15,23,42,0.1)",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(0, 0, 160, 0.1)"
        : state.isFocused
          ? "rgba(0, 0, 160, 0.05)"
          : "white",
      color: state.isSelected ? "#bb1ccc" : "#374151",
      fontSize: 12,
      "&:active": {
        backgroundColor: "rgba(0, 0, 160, 0.15)",
      },
    }),
    input: (provided) => ({
      ...provided,
      fontSize: 10,
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: 10,
    }),
  };

  const toOptions = (arr, capitalize = false) => {
    if (!arr || !Array.isArray(arr)) return [];
    return arr?.map((v) => {
      if (typeof v === "object" && v?.type && v?.description) {
        return { label: v?.description, value: v?.type };
      }
      if (typeof v === "object" && v?.label && v?.value) {
        return v;
      }
      // Capitalize first letter if flag is true
      const label =
        capitalize && typeof v === "string"
          ? v.charAt(0).toUpperCase() + v.slice(1).toUpperCase()
          : v;
      return { label: label, value: v };
    }).sort((a, b) => {
      const labelA = String(a.label || '').toLowerCase();
      const labelB = String(b.label || '').toLowerCase();
      return labelA.localeCompare(labelB);
    });
  };
  const fetchTables = async () => {
    setLoadingTables(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/search-by-table-column`,
        {
          searchType: "table",
        },
      );

      if (response.data && response.data.data) {
        const tables = response.data.data.map((item) => item.data_asset_name);
        setTablesFromAPI(tables);
      }
    } catch (error) {
      console.error("Error fetching tables:", error);
      setTablesFromAPI([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const fetchColumns = async (isCDE = null) => {
    setLoadingColumns(true);
    try {
      const payload = {
        searchType: "column",
      };

      if (isCDE === true) {
        payload.isCDE = true;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/search-by-table-column`,
        payload,
      );

      if (response.data && response.data.data) {
        setColumnsFromAPI(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching columns:", error);
      setColumnsFromAPI([]);
    } finally {
      setLoadingColumns(false);
    }
  };

  useEffect(() => {
    setExpandedSection(selectedTab === "glossary" ? "Glossary Filters" : "Product Segment")
  }, [selectedTab])

  useEffect(() => {
    if (open && localFilters.searchBy === "tables") {
      fetchTables();
    }
  }, [open, localFilters.searchBy]);

  useEffect(() => {
    if (open && localFilters.searchBy === "columns") {
      fetchColumns(localFilters.cdeEnabled ? true : null);
    }
  }, [open, localFilters.searchBy, localFilters.cdeEnabled]);

  const getFullOptions = useMemo(() => {
    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;

    if (!parentDomains) {
      return {
        parentDomains: filterOptions?.parentDomains || [],
        domains: filterOptions?.domains || [],
        subDomains: filterOptions?.subDomains || [],
        products: filterOptions?.products || [],
      };
    }

    const parentDomainsList = parentDomains.map((pd) => pd.parent_domain);

    const allDomains = [];
    parentDomains.forEach((pd) => {
      (pd.domains || []).forEach((d) => {
        if (!allDomains.includes(d.domain)) {
          allDomains.push(d.domain);
        }
      });
    });

    const allSubDomains = [];
    parentDomains.forEach((pd) => {
      (pd.domains || []).forEach((d) => {
        (d.sub_domains || []).forEach((sd) => {
          if (!allSubDomains.includes(sd.sub_domain)) {
            allSubDomains.push(sd.sub_domain);
          }
        });
      });
    });

    const allProducts = [];
    parentDomains.forEach((pd) => {
      (pd.domains || []).forEach((d) => {
        (d.sub_domains || []).forEach((sd) => {
          (sd.products || []).forEach((p) => {
            if (!allProducts.includes(p)) {
              allProducts.push(p);
            }
          });
        });
      });
    });

    return {
      parentDomains: parentDomainsList,
      domains: allDomains,
      subDomains: allSubDomains,
      products: allProducts,
    };
  }, [hierarchicalFilters, filterOptions]);
  const filteredDomainOptions = useMemo(() => {
    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;

    if (!parentDomains) return toOptions(filterOptions?.domains || []);

    const result = [];
    parentDomains.forEach((pd) => {
      if (localFilters.parentDomain.length > 0 && !localFilters.parentDomain.includes(pd.parent_domain_id)) return;
      (pd.domains || []).forEach((d) => {
        if (!result.some((r) => r.value === d.domain_id)) {
          result.push({ label: d.domain, value: d.domain_id });
        }
      });
    });

    return result.sort((a, b) => String(a.label || '').toLowerCase().localeCompare(String(b.label || '').toLowerCase()));
  }, [hierarchicalFilters, localFilters.parentDomain, filterOptions?.domains]);

  const filteredSubDomainOptions = useMemo(() => {
    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;

    if (!parentDomains) return toOptions(filterOptions?.subDomains || []);

    const result = [];
    parentDomains.forEach((pd) => {
      if (localFilters.parentDomain.length > 0 && !localFilters.parentDomain.includes(pd.parent_domain_id)) return;
      (pd.domains || []).forEach((d) => {
        if (localFilters.domain.length > 0 && !localFilters.domain.includes(d.domain_id)) return;
        (d.sub_domains || []).forEach((sd) => {
          if (!result.some((r) => r.value === sd.sub_domain_id)) {
            result.push({ label: sd.sub_domain, value: sd.sub_domain_id });
          }
        });
      });
    });

    return result.sort((a, b) => String(a.label || '').toLowerCase().localeCompare(String(b.label || '').toLowerCase()));
  }, [
    hierarchicalFilters,
    localFilters.parentDomain,
    localFilters.domain,
    filterOptions?.subDomains,
  ]);

  const filteredProductOptions = useMemo(() => {
    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;

    if (!parentDomains) {
      return toOptions(getFullOptions.products);
    }

    // If no filters selected, show all
    if (
      localFilters.parentDomain.length === 0 &&
      localFilters.domain.length === 0 &&
      localFilters.subDomain.length === 0
    ) {
      return toOptions(getFullOptions.products);
    }

    const filteredProducts = [];
    parentDomains.forEach((pd) => {
      // Check parent domain filter
      if (
        localFilters.parentDomain.length > 0 &&
        !localFilters.parentDomain.includes(pd.parent_domain_id)
      ) {
        return;
      }

      (pd.domains || []).forEach((d) => {
        // Check domain filter
        if (
          localFilters.domain.length > 0 &&
          !localFilters.domain.includes(d.domain_id)
        ) {
          return;
        }

        (d.sub_domains || []).forEach((sd) => {
          // Check sub domain filter
          if (
            localFilters.subDomain.length > 0 &&
            !localFilters.subDomain.includes(sd.sub_domain_id)
          ) {
            return;
          }

          (sd.products || []).forEach((p) => {
            if (!filteredProducts.includes(p)) {
              filteredProducts.push(p);
            }
          });
        });
      });
    });

    return toOptions(filteredProducts);
  }, [
    hierarchicalFilters,
    localFilters.parentDomain,
    localFilters.domain,
    localFilters.subDomain,
    getFullOptions.products,
  ]);

  // Market Coverage filtered options
  const filteredRegionOptions = useMemo(() => {
    const marketCoverage = filterOptions?.marketCoverage || [];

    if (localFilters.parentRegion.length === 0) {
      const regions = marketCoverage.flatMap((mc) =>
        mc.regions.map((r) => r.region),
      );
      const uniqueRegions = [...new Set(regions)];
      return toOptions(uniqueRegions);
    }

    const filteredRegions = [];
    marketCoverage.forEach((mc) => {
      if (localFilters.parentRegion.includes(mc.parent_region)) {
        mc.regions.forEach((r) => {
          if (!filteredRegions.includes(r.region)) {
            filteredRegions.push(r.region);
          }
        });
      }
    });

    return toOptions(filteredRegions);
  }, [filterOptions?.marketCoverage, localFilters.parentRegion]);

  const filteredCountryOptions = useMemo(() => {
    const marketCoverage = filterOptions?.marketCoverage || [];

    if (
      localFilters.parentRegion.length === 0 &&
      localFilters.region.length === 0
    ) {
      const countries = marketCoverage.flatMap((mc) =>
        mc.regions.flatMap((r) => r.countries),
      );
      const uniqueCountries = [...new Set(countries)];
      return toOptions(uniqueCountries);
    }

    const filteredCountries = [];
    marketCoverage.forEach((mc) => {
      if (
        localFilters.parentRegion.length > 0 &&
        !localFilters.parentRegion.includes(mc.parent_region)
      ) {
        return;
      }

      mc.regions.forEach((r) => {
        if (
          localFilters.region.length > 0 &&
          !localFilters.region.includes(r.region)
        ) {
          return;
        }

        r.countries.forEach((c) => {
          if (!filteredCountries.includes(c)) {
            filteredCountries.push(c);
          }
        });
      });
    });

    return toOptions(filteredCountries);
  }, [
    filterOptions?.marketCoverage,
    localFilters.parentRegion,
    localFilters.region,
  ]);

  const parentDomainOptions = useMemo(() => {
    const parentDomains = hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;
    if (!parentDomains) return toOptions(filterOptions?.parentDomains || []);
    return parentDomains
      .map((pd) => ({ label: pd.parent_domain, value: pd.parent_domain_id }))
      .sort((a, b) => String(a.label || '').toLowerCase().localeCompare(String(b.label || '').toLowerCase()));
  }, [hierarchicalFilters, filterOptions?.parentDomains]);

  const productDataLayerOptions = useMemo(
    () => toOptions(filterOptions?.productDataLayer || []),
    [filterOptions?.productDataLayer],
  );

  const lineOfBusinessOptions = useMemo(
    () =>
      toOptions(filterOptions?.lineOfBusiness || []).map((opt) => ({
        ...opt,
        label: opt.label?.toUpperCase(), // "mw" → "MW"
      })),
    [filterOptions?.lineOfBusiness],
  );

  const reportTypeOptions = useMemo(
    () => toOptions(filterOptions?.reportType || []),
    [filterOptions?.reportType],
  );

  const cloudProviderOptions = useMemo(
    () =>
      toOptions(filterOptions?.cloudProvider || []).map((opt) => ({
        ...opt,
        label: opt.label?.toUpperCase(),
      })),
    [filterOptions?.cloudProvider],
  );

  const tagsOptions = useMemo(
    () => toOptions(filterOptions?.tags || []),
    [filterOptions?.tags],
  );

  const parentRegionOptions = useMemo(() => {
    const marketCoverage = filterOptions?.marketCoverage || [];
    const parentRegions = marketCoverage.map((mc) => mc.parent_region);
    return toOptions(parentRegions);
  }, [filterOptions?.marketCoverage]);

  const expectedUserBaseRange = useMemo(
    () => filterOptions?.expectedUserBase || { min: 0, max: 10000 },
    [filterOptions?.expectedUserBase],
  );

  const dqRatingRange = useMemo(
    () => filterOptions?.dqRating || { min: 0, max: 100 },
    [filterOptions?.dqRating],
  );

  const productOwnerOptions = useMemo(() => {
    const owners = filterOptions?.productOwner || [];
    return owners.map((email) => ({
      label: formatEmailToName(email),
      value: email,
    }));
  }, [filterOptions?.productOwner]);

  const assetSourceOptions = useMemo(
    () => toOptions(filterOptions?.assetSource || []),
    [filterOptions?.assetSource],
  );

  const reportOwnerOptions = useMemo(() => {
    const owners = filterOptions?.reportOwner || [];
    return owners.map((email) => ({
      label: formatEmailToName(email),
      value: email,
    }));
  }, [filterOptions?.reportOwner]);

  const tablesSearchOptions = useMemo(() => {
    return toOptions(tablesFromAPI);
  }, [tablesFromAPI]);

  const columnsSearchOptions = useMemo(() => {
    return columnsFromAPI.map((col) => ({
      label: col.column_name,
      value: col.column_name,
      is_cde: col.is_cde,
    }));
  }, [columnsFromAPI]);

  // ---------------------------
  // GLOSSARY OPTIONS
  // ---------------------------

  const glossaryStatusOptions = useMemo(
    () => toOptions(glossaryFilterOptions?.status || []),
    [glossaryFilterOptions]
  );

  const glossaryAssetTypeOptions = useMemo(
    () => toOptions(glossaryFilterOptions?.assetType || []),
    [glossaryFilterOptions]
  );

  const glossaryDomainOptions = useMemo(
    () => toOptions(glossaryFilterOptions?.domain || []),
    [glossaryFilterOptions]
  );

  // ---------------------------
  // INITIALIZE FILTERS
  // ---------------------------
  useEffect(() => {
    if (!open) return;

    if (selectedTab === "glossary") {
      setLocalFilters((prev) => ({
        ...prev,
        status: glossaryFilters?.status || [],
        assetType: glossaryFilters?.assetType || [],
        glossaryDomain: glossaryFilters?.domain || [],
      }));
      setExpandedSection("Glossary Filters");
    } else {
      setLocalFilters((prev) => ({
        ...prev,
        parentDomain: allSelectedFilters.parentDomain || [],
        domain: allSelectedFilters.domain || [],
        subDomain: allSelectedFilters.subDomain || [],
        product: allSelectedFilters.product || [],
        productDataLayer: allSelectedFilters.productDataLayer || [],
        lineOfBusiness: allSelectedFilters.lineOfBusiness || [],
        reportType: allSelectedFilters.reportType || [],
        cloudProvider: allSelectedFilters.cloudProvider || [],
        tags: allSelectedFilters.tags || [],
        parentRegion: allSelectedFilters.parentRegion || [],
        region: allSelectedFilters.region || [],
        country: allSelectedFilters.country || [],
        expectedUserBase: allSelectedFilters.expectedUserBase || [
          expectedUserBaseRange.min,
          expectedUserBaseRange.max,
        ],
        dqRating: allSelectedFilters.dqRating || [
          dqRatingRange.min,
          dqRatingRange.max,
        ],
        productOwner: allSelectedFilters.productOwner || [],
        assetSource: allSelectedFilters.assetSource || [],
        reportOwner: allSelectedFilters.reportOwner || [],
        searchBy: allSelectedFilters.searchBy || "tables",
        cdeEnabled: allSelectedFilters.cdeEnabled || false,
        tables: allSelectedFilters.tables || [],
        columns: allSelectedFilters.columns || [],
        searchQuery: allSelectedFilters.searchQuery || "",
      }));
      setExpandedSection("Product Segment");
    }
  }, [
    open,
    allSelectedFilters,
    expectedUserBaseRange.min,
    expectedUserBaseRange.max,
    dqRatingRange.min,
    dqRatingRange.max,
  ]);

  const updateLocalFilter = (key, value) => {
    if (Array.isArray(value) && value.every((v) => typeof v === "number")) {
      setLocalFilters((prev) => ({ ...prev, [key]: value }));
    } else {
      const values = value ? value.map((opt) => opt.value) : [];
      setLocalFilters((prev) => ({ ...prev, [key]: values }));
    }
  };

  const findProductHierarchy = (selectedProduct) => {
    if (!hierarchicalFilters || !selectedProduct) {
      return null;
    }

    const parentDomains =
      hierarchicalFilters.parent_domains || hierarchicalFilters.parentDomains;

    if (!parentDomains) {
      return null;
    }

    for (const pd of parentDomains) {
      for (const d of pd.domains || []) {
        for (const sd of d.sub_domains || []) {
          if (sd.products?.includes(selectedProduct)) {
            return {
              parentDomain: pd.parent_domain_id,
              domain: d.domain_id,
              subDomain: sd.sub_domain_id,
              product: selectedProduct,
            };
          }
        }
      }
    }

    return null;
  };

  const findMarketHierarchy = (selectedCountry) => {
    if (!filterOptions?.marketCoverage || !selectedCountry) return null;

    for (const mc of filterOptions.marketCoverage) {
      for (const r of mc.regions || []) {
        if (r.countries?.includes(selectedCountry)) {
          return {
            parentRegion: mc.parent_region,
            region: r.region,
            country: selectedCountry,
          };
        }
      }
    }
    return null;
  };

  const handleParentDomainSelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];

    if (values.length === 0) {
      setLocalFilters((prev) => ({
        ...prev,
        parentDomain: values,
      }));
    } else {
      const parentDomains =
        hierarchicalFilters?.parent_domains ||
        hierarchicalFilters?.parentDomains;

      let validDomains = [];
      let validSubDomains = [];
      let validProducts = [];

      if (parentDomains) {
        parentDomains.forEach((pd) => {
          if (values.includes(pd.parent_domain_id)) {
            (pd.domains || []).forEach((d) => {
              if (localFilters.domain.includes(d.domain_id)) {
                validDomains.push(d.domain_id);
              }
              (d.sub_domains || []).forEach((sd) => {
                if (localFilters.subDomain.includes(sd.sub_domain_id)) {
                  validSubDomains.push(sd.sub_domain_id);
                }
                (sd.products || []).forEach((p) => {
                  if (localFilters.product.includes(p)) {
                    validProducts.push(p);
                  }
                });
              });
            });
          }
        });
      }

      setLocalFilters((prev) => ({
        ...prev,
        parentDomain: values,
        domain: validDomains,
        subDomain: validSubDomains,
        product: validProducts,
      }));
    }
  };

  const handleDomainSelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];
    setLocalFilters((prev) => ({ ...prev, domain: values }));
  };

  const handleSubDomainSelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];
    setLocalFilters((prev) => ({ ...prev, subDomain: values }));
  };

  const handleProductSelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];
    setLocalFilters((prev) => ({ ...prev, product: values }));
  };

  const handleParentRegionSelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];

    if (values.length === 0) {
      setLocalFilters((prev) => ({
        ...prev,
        parentRegion: values,
      }));
    } else {
      const marketCoverage = filterOptions?.marketCoverage || [];

      let validRegions = [];
      let validCountries = [];

      marketCoverage.forEach((mc) => {
        if (values.includes(mc.parent_region)) {
          mc.regions.forEach((r) => {
            if (localFilters.region.includes(r.region)) {
              validRegions.push(r.region);
            }
            r.countries.forEach((c) => {
              if (localFilters.country.includes(c)) {
                validCountries.push(c);
              }
            });
          });
        }
      });

      setLocalFilters((prev) => ({
        ...prev,
        parentRegion: values,
        region: validRegions,
        country: validCountries,
      }));
    }
  };

  const handleRegionSelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];

    if (values.length > 0 && filterOptions?.marketCoverage) {
      const parentRegions = new Set(localFilters.parentRegion || []);
      let validCountries = [];

      values.forEach((selectedRegion) => {
        const parentMC = filterOptions.marketCoverage.find((mc) =>
          mc.regions?.some((r) => r.region === selectedRegion),
        );

        if (parentMC) {
          parentRegions.add(parentMC.parent_region);

          const regionObj = parentMC.regions.find(
            (r) => r.region === selectedRegion,
          );
          if (regionObj) {
            regionObj.countries.forEach((c) => {
              if (localFilters.country.includes(c)) {
                validCountries.push(c);
              }
            });
          }
        }
      });

      setLocalFilters((prev) => ({
        ...prev,
        parentRegion: Array.from(parentRegions),
        region: values,
        country: validCountries,
      }));
    } else {
      setLocalFilters((prev) => ({ ...prev, region: values }));
    }
  };

  const handleCountrySelect = (value) => {
    const values = value ? value.map((opt) => opt.value) : [];

    if (values.length > 0 && filterOptions?.marketCoverage) {
      const parentRegions = new Set(localFilters.parentRegion || []);
      const regions = new Set(localFilters.region || []);

      values.forEach((selectedCountry) => {
        const hierarchy = findMarketHierarchy(selectedCountry);

        if (hierarchy) {
          parentRegions.add(hierarchy.parentRegion);
          regions.add(hierarchy.region);
        }
      });

      setLocalFilters((prev) => ({
        ...prev,
        parentRegion: Array.from(parentRegions),
        region: Array.from(regions),
        country: values,
      }));
    } else {
      setLocalFilters((prev) => ({ ...prev, country: values }));
    }
  };
  const autoFillProductHierarchy = () => {
    if (localFilters.product.length === 0) return;

    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;
    if (!parentDomains) return;

    const parentDomainsSet = new Set(localFilters.parentDomain || []);
    const domainsSet = new Set(localFilters.domain || []);
    const subDomainsSet = new Set(localFilters.subDomain || []);

    localFilters.product.forEach((selectedProduct) => {
      const hierarchy = findProductHierarchy(selectedProduct);
      if (hierarchy) {
        parentDomainsSet.add(hierarchy.parentDomain);
        domainsSet.add(hierarchy.domain);
        subDomainsSet.add(hierarchy.subDomain);
      }
    });

    setLocalFilters((prev) => ({
      ...prev,
      parentDomain: Array.from(parentDomainsSet),
      domain: Array.from(domainsSet),
      subDomain: Array.from(subDomainsSet),
    }));
  };

  const autoFillSubDomainHierarchy = () => {
    if (localFilters.subDomain.length === 0) return;

    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;
    if (!parentDomains) return;

    const parentDomainsSet = new Set(localFilters.parentDomain || []);
    const domainsSet = new Set(localFilters.domain || []);

    localFilters.subDomain.forEach((selectedSubDomainId) => {
      for (const pd of parentDomains) {
        for (const d of pd.domains || []) {
          const foundSD = d.sub_domains?.find(
            (sd) => sd.sub_domain_id === selectedSubDomainId,
          );
          if (foundSD) {
            parentDomainsSet.add(pd.parent_domain_id);
            domainsSet.add(d.domain_id);
            break;
          }
        }
      }
    });

    setLocalFilters((prev) => ({
      ...prev,
      parentDomain: Array.from(parentDomainsSet),
      domain: Array.from(domainsSet),
    }));
  };

  const autoFillDomainHierarchy = () => {
    if (localFilters.domain.length === 0) return;

    const parentDomains =
      hierarchicalFilters?.parent_domains || hierarchicalFilters?.parentDomains;
    if (!parentDomains) return;

    const parentDomainsSet = new Set(localFilters.parentDomain || []);

    localFilters.domain.forEach((selectedDomainId) => {
      const parentDom = parentDomains.find((pd) =>
        pd.domains?.some((d) => d.domain_id === selectedDomainId),
      );

      if (parentDom) {
        parentDomainsSet.add(parentDom.parent_domain_id);
      }
    });

    setLocalFilters((prev) => ({
      ...prev,
      parentDomain: Array.from(parentDomainsSet),
    }));
  };
  const handleApplyFilters = () => {
    // GLOSSARY APPLY
    if (selectedTab === "glossary") {

      handleApplyGlossaryFilters({
        status: localFilters.status || [],
        assetType: localFilters.assetType || [],
        domain: localFilters.glossaryDomain || [],
      });

      onClose();
      return;
    }

    if (handleApplyAllFilters && typeof handleApplyAllFilters === "function") {
      const allFilters = {
        parentDomain:
          localFilters.parentDomain.length > 0 ? localFilters.parentDomain : [],
        domain: localFilters.domain.length > 0 ? localFilters.domain : [],
        subDomain:
          localFilters.subDomain.length > 0 ? localFilters.subDomain : [],
        product: localFilters.product.length > 0 ? localFilters.product : [],
        searchValue: searchQuery || "",
        productDataLayer: localFilters.productDataLayer || [],
        lineOfBusiness: localFilters.lineOfBusiness || [],
        reportType: localFilters.reportType || [],
        cloudProvider: localFilters.cloudProvider || [],
        tags: localFilters.tags || [],
        searchBy: localFilters.searchBy || "tables",
        tables: localFilters.tables || [],
        columns: localFilters.columns || [],
        cdeEnabled: localFilters.cdeEnabled || false,
        parentRegion: localFilters.parentRegion || [],
        marketRegion: localFilters.region || [],
        country: localFilters.country || [],
        expectedUserBase: localFilters.expectedUserBase || [0, 10000],
        dqRating: localFilters.dqRating || [0, 100],
        productOwner: localFilters.productOwner || [],
        assetSource: localFilters.assetSource || [],
        reportOwner: localFilters.reportOwner || [],
      };

      handleApplyAllFilters(allFilters);
    } else {
      if (localFilters.parentDomain.length > 0) {
        handleParentDomainChange({
          target: { value: localFilters.parentDomain[0] },
        });
      } else {
        handleParentDomainChange({ target: { value: "All Parent Domains" } });
      }

      if (localFilters.domain.length > 0) {
        handleDomainChange({ target: { value: localFilters.domain[0] } });
      } else {
        handleDomainChange({ target: { value: "All Domains" } });
      }

      if (localFilters.subDomain.length > 0) {
        handleSubDomainChange({ target: { value: localFilters.subDomain[0] } });
      } else {
        handleSubDomainChange({ target: { value: "All Sub Domains" } });
      }

      if (localFilters.product.length > 0) {
        handleProductChange({ target: { value: localFilters.product[0] } });
      } else {
        handleProductChange({ target: { value: "All Products" } });
      }
    }

    onClose();
  };

  const handleReset = () => {
    if (selectedTab === "glossary") {
      setLocalFilters((prev) => ({
        ...prev,
        status: [],
        assetType: [],
        glossaryDomain: [],
      }));
      handleApplyGlossaryFilters({
        status: [],
        assetType: [],
        glossaryDomain: [],
      })
      return;
    }

    setLocalFilters({
      parentDomain: [],
      domain: [],
      subDomain: [],
      product: [],
      productDataLayer: [],
      lineOfBusiness: [],
      reportType: [],
      cloudProvider: [],
      tags: [],
      parentRegion: [],
      region: [],
      country: [],
      expectedUserBase: [expectedUserBaseRange.min, expectedUserBaseRange.max],
      dqRating: [dqRatingRange.min, dqRatingRange.max],
      productOwner: [],
      assetSource: [],
      reportOwner: [],
      searchBy: "tables",
      cdeEnabled: false,
      tables: [],
      columns: [],
      searchQuery: "",
    });
    handleResetFilters();
    // onClose();
  };

  const toggleSection = (title) => {
    setExpandedSection((prev) => (prev === title ? null : title));
  };

  const renderSearchByFilter = () => {
    const searchOptions =
      localFilters.searchBy === "tables"
        ? tablesSearchOptions
        : columnsSearchOptions;

    const isLoading =
      localFilters.searchBy === "tables" ? loadingTables : loadingColumns;

    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          width: "104%",
          transform: "translateX(-2%)",
          boxSizing: "border-box",
        }}
      >
        {/* Header with label */}
        <Typography fontSize={12} fontWeight={700} mb={2} color="#1F2937">
          Search by Tables/Columns
        </Typography>

        {/* Toggle between Tables and Columns */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              p: 0.5,
              backgroundColor: "#F3F4F6",
              borderRadius: "8px",
            }}
          >
            <button
              onClick={() => {
                setLocalFilters((prev) => ({
                  ...prev,
                  searchBy: "tables",
                  columns: [],
                  cdeEnabled: false,
                }));
              }}
              style={{
                flex: 1,
                padding: "10px 16px",
                backgroundColor:
                  localFilters.searchBy === "tables" ? "#fff" : "transparent",
                color:
                  localFilters.searchBy === "tables"
                    ? PRIMARY_COLOR
                    : "#6B7280",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
                transition: "all 0.2s",
                boxShadow:
                  localFilters.searchBy === "tables"
                    ? "0 1px 3px rgba(0, 0, 0, 0.1)"
                    : "none",
              }}
            >
              Tables
            </button>
            <button
              onClick={() => {
                setLocalFilters((prev) => ({
                  ...prev,
                  searchBy: "columns",
                  tables: [],
                }));
              }}
              style={{
                flex: 1,
                padding: "10px 16px",
                backgroundColor:
                  localFilters.searchBy === "columns" ? "#fff" : "transparent",
                color:
                  localFilters.searchBy === "columns"
                    ? PRIMARY_COLOR
                    : "#6B7280",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
                transition: "all 0.2s",
                boxShadow:
                  localFilters.searchBy === "columns"
                    ? "0 1px 3px rgba(0, 0, 0, 0.1)"
                    : "none",
              }}
            >
              Columns
            </button>
          </Box>
        </Box>

        {/* CDE Toggle - Only show for Columns */}
        {localFilters.searchBy === "columns" && (
          <Box
            sx={{
              mb: 2,
              p: 1,
              backgroundColor: "#F9FAFB",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography fontSize={12} fontWeight={600} color="#374151">
              CDE Only
            </Typography>
            <Box
              onClick={() => {
                const newCDEValue = !localFilters.cdeEnabled;
                setLocalFilters((prev) => ({
                  ...prev,
                  cdeEnabled: newCDEValue,
                  columns: [],
                }));
              }}
              sx={{
                width: 44,
                height: 24,
                borderRadius: "12px",
                backgroundColor: localFilters.cdeEnabled
                  ? PRIMARY_COLOR
                  : "#D1D5DB",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.2s",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: localFilters.cdeEnabled
                    ? "#000080"
                    : "#9CA3AF",
                },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  position: "absolute",
                  top: 3,
                  left: localFilters.cdeEnabled ? 23 : 3,
                  transition: "left 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </Box>
          </Box>
        )}

        {/* Dropdown for selecting Tables or Columns - Wrapped in Box with position relative */}
        <div className="!z-99999">
          <Box sx={{ position: "relative" }}>
            <CustomMultiSelect
              label={
                localFilters.searchBy === "tables"
                  ? "Select Tables"
                  : "Select Columns"
              }
              options={searchOptions}
              value={searchOptions.filter((o) =>
                localFilters[localFilters.searchBy]?.includes(o.value),
              )}
              onChange={(e, value) => {
                const values = value ? value.map((opt) => opt.value) : [];
                setLocalFilters((prev) => ({
                  ...prev,
                  [prev.searchBy]: values,
                }));
              }}
              placeholder={
                isLoading
                  ? `Loading ${localFilters.searchBy === "tables" ? "Tables" : "Columns"}...`
                  : `Select ${localFilters.searchBy === "tables" ? "Tables" : "Columns"}`
              }
              loading={isLoading}
              noOptionsText={
                isLoading
                  ? "Loading..."
                  : `No ${localFilters.searchBy === "tables" ? "tables" : "columns"} found`
              }
            />

            {/* Item count */}
            {!isLoading && searchOptions.length > 0 && (
              <Typography fontSize={10} color="text.secondary" mt={0.5}>
                {searchOptions.length}{" "}
                {localFilters.searchBy === "tables" ? "tables" : "columns"}{" "}
                available
                {localFilters.searchBy === "columns" &&
                  localFilters.cdeEnabled &&
                  " (CDE only)"}
              </Typography>
            )}
          </Box>
        </div>
      </Box>
    );
  };

  const renderSelect = (
    label,
    key,
    options,
    disabled = false,
    customOnChange = null,
    onMenuClose = null,
  ) => {
    if (!options || options.length === 0) return null;

    return (
      <CustomMultiSelect
        label={label}
        options={options}
        value={
          localFilters[key]
            ? options.filter((o) => localFilters[key].includes(o.value))
            : []
        }
        onChange={(e, v) => {
          if (customOnChange) {
            customOnChange(v);
          } else {
            updateLocalFilter(key, v);
          }
        }}
        onClose={onMenuClose}
        placeholder={`Select`}
        disabled={disabled}
        noOptionsText={`No ${label.toLowerCase()} found`}
      />
    );
  };
  const renderRangeSlider = (label, key, min, max) => (
    <Box sx={{ mb: 3 }} key={key}>
      <Typography fontSize={12} fontWeight={600} mb={1} color="#374151">
        {label}
      </Typography>
      <Slider
        value={localFilters[key] || [min, max]}
        onChange={(_, value) => updateLocalFilter(key, value)}
        valueLabelDisplay="auto"
        min={min}
        max={max}
        sx={{
          color: PRIMARY_COLOR,
          "& .MuiSlider-thumb": {
            backgroundColor: PRIMARY_COLOR,
          },
          "& .MuiSlider-track": {
            backgroundColor: PRIMARY_COLOR,
          },
        }}
      />
      <Typography fontSize={10} color="text.secondary">
        Range: {(localFilters[key] || [min, max]).join(" - ")}
      </Typography>
    </Box>
  );

  const renderAccordion = (title, content) => {
    const hasContent = React.Children.toArray(content).some(
      (child) => child !== null,
    );
    if (!hasContent) return null;

    const isOpen = expandedSection === title;

    return (
      <Box
        key={title}
        sx={{ mb: 1, border: "1px solid #E5E7EB", borderRadius: "10px" }}
      >
        {/* Header row */}
        <Box
          onClick={() => toggleSection(title)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 2.5,
            cursor: "pointer",
            userSelect: "none",
            borderRadius: "5px",
            ":hover": {
              backgroundColor: "#f5f3ff",
            },
          }}
        >
          <div className="font-[13px font-bold flex items-center gap-2">
            <div
              className={
                expandedSection === title
                  ? "bg-[#6e72d9] text-white h-[32px] w-[32px] flex items-center justify-center rounded-lg"
                  : "text-[#9596e1]"
              }
            >
              <Logs className="h-5 " />
            </div>
            <p>{title}</p>
          </div>

          <ChevronRightIcon
            sx={{
              fontSize: 18,
              color: "#6B7280",
              transition: "transform 0.2s ease",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Box>

        {/* Collapsible content */}
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 2, px: 2, backgroundColor: "#fbfcff", maxHeight: "280px", overflow: "auto" }}>{content}</Box>
        </Collapse>
      </Box>
    );
  };

  const NAV_SECTIONS = selectedTab === "glossary"
    ? [{ title: "Glossary Filters", icon: <BookOpen size={18} /> }]
    : [
        { title: "Product Segment",       icon: <Layers size={18} /> },
        { title: "Business Context",      icon: <Briefcase size={18} /> },
        { title: "Market Coverage",       icon: <Globe size={18} /> },
        { title: "Usage & Adoption",      icon: <TrendingUp size={18} /> },
        { title: "Ownership & Governance",icon: <ShieldCheck size={18} /> },
      ];

  const renderSectionContent = () => {
    if (selectedTab === "glossary") {
      return (
        <>
          {renderSelect("Status", "status", glossaryStatusOptions)}
          {renderSelect("Asset Type", "assetType", glossaryAssetTypeOptions)}
          {renderSelect("Domain", "glossaryDomain", glossaryDomainOptions)}
        </>
      );
    }
    switch (expandedSection) {
      case "Product Segment":
        return (
          <>
            {renderSelect("Parent Domain", "parentDomain", parentDomainOptions, false, handleParentDomainSelect)}
            {renderSelect("Domain", "domain", filteredDomainOptions, false, handleDomainSelect, autoFillDomainHierarchy)}
            {renderSelect("Sub Domain", "subDomain", filteredSubDomainOptions, false, handleSubDomainSelect, autoFillSubDomainHierarchy)}
            {renderSelect("Product", "product", filteredProductOptions, false, handleProductSelect, autoFillProductHierarchy)}
          </>
        );
      case "Business Context":
        return (
          <>
            {selectedTab !== "report" && renderSearchByFilter()}
            {selectedTab !== "report" && renderSelect("Product Data Layer", "productDataLayer", productDataLayerOptions)}
            {renderSelect("Line of Business", "lineOfBusiness", lineOfBusinessOptions)}
            {selectedTab === "report" && renderSelect("Report Type", "reportType", reportTypeOptions)}
            {selectedTab !== "report" && renderSelect("Cloud Provider", "cloudProvider", cloudProviderOptions)}
            {renderSelect("Tags", "tags", tagsOptions)}
          </>
        );
      case "Market Coverage":
        return (
          <>
            {renderSelect("Parent Region", "parentRegion", parentRegionOptions, false, handleParentRegionSelect)}
            {renderSelect("Region", "region", filteredRegionOptions, false, handleRegionSelect)}
            {renderSelect("Country", "country", filteredCountryOptions, false, handleCountrySelect)}
          </>
        );
      case "Usage & Adoption":
        return (
          <>
            {renderRangeSlider("Expected User Base", "expectedUserBase", expectedUserBaseRange.min, expectedUserBaseRange.max)}
            {selectedTab !== "report" && renderRangeSlider("DQ Rating", "dqRating", dqRatingRange.min, dqRatingRange.max)}
          </>
        );
      case "Ownership & Governance":
        return (
          <>
            {renderSelect("Product Owner", "productOwner", productOwnerOptions)}
            {selectedTab !== "report" && renderSelect("Asset Source", "assetSource", assetSourceOptions)}
            {selectedTab === "report" && renderSelect("Report Owner", "reportOwner", reportOwnerOptions)}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 380,
          background: "#f9fafb",
          height: "calc(100% - 66px)",
          display: "flex",
          flexDirection: "column",
          marginTop: "66px",
        }}
      >
        {/* Header */}
        <div className="px-3 py-5 bg-[#bb1ccc]">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="flex items-center gap-3 text-white">
              <div className="bg-[#bb1ccc] text-white px-2 py-1 rounded-md hover:animate-spin">
                <ListFilter className="h-8 text" />
              </div>
              <div className="flex flex-col">
                <p className="text-lg font-bold text-white">Advanced Filters</p>
                <span>Refine your search criteria</span>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </Box>
        </div>

        {/* Apply / Reset — top */}
        <Box sx={{ display: "flex", gap: 2, px: 3, py: 1.5, backgroundColor: "#fff", borderBottom: "1px solid #E5E7EB" }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReset}
            sx={{
              borderColor: "#D1D5DB",
              color: "#374151",
              textTransform: "none",
              py: 1,
              borderRadius: "8px",
              fontSize: "10px",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <RotateCcw style={{ width: "14px", height: "14px" }} />
            Reset
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              backgroundColor: "#bb1ccc",
              textTransform: "none",
              py: 1,
              borderRadius: "8px",
              fontSize: "10px",
              boxShadow: "none",
              gap: 1,
            }}
          >
            <CircleCheck style={{ width: "14px", height: "14px" }} />
            Apply
          </Button>
        </Box>

        {/* Icon navigation bar — only for non-glossary tabs */}
        {selectedTab !== "glossary" && <Box sx={{ px: 3, py: 1.5, backgroundColor: "#fff", borderBottom: "1px solid #E5E7EB" }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            backgroundColor: "#F3F4F6",
            borderRadius: "999px",
            padding: "6px 8px",
            gap: "4px",
          }}>
            {NAV_SECTIONS.map((section) => {
              const isActive = expandedSection === section.title;
              const isHovered = hoveredSection === section.title;
              return (
                <div
                  key={section.title}
                  style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}
                  onMouseEnter={() => setHoveredSection(section.title)}
                  onMouseLeave={() => setHoveredSection(null)}
                >
                  <button
                    onClick={() => setExpandedSection(section.title)}
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "10px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: isActive ? "#6366f2" : isHovered ? "#e0e7ff" : "#fff",
                      color: isActive ? "#fff" : isHovered ? "#6366f2" : "#9CA3AF",
                      transition: "all 0.18s ease",
                      boxShadow: isActive ? "0 2px 8px rgba(99,102,242,0.3)" : "none",
                    }}
                  >
                    {section.icon}
                  </button>
                  {/* Tooltip */}
                  {isHovered && (
                    <div style={{
                      position: "absolute",
                      bottom: "calc(100% + 8px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "#1F2937",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 500,
                      padding: "4px 8px",
                      borderRadius: "6px",
                      whiteSpace: "nowrap",
                      zIndex: 9999,
                      pointerEvents: "none",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}>
                      {section.title}
                      <div style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        borderWidth: "4px",
                        borderStyle: "solid",
                        borderColor: "#1F2937 transparent transparent transparent",
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Active section label */}
          {expandedSection && (
            <p style={{ fontSize: "11px", fontWeight: 600, color: "#6366f2", marginTop: "8px", textAlign: "center", letterSpacing: "0.02em" }}>
              {expandedSection}
            </p>
          )}
        </Box>}

        {/* Filter content for selected section */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          {renderSectionContent()}
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
